import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import QText from './QText';
import InvoiceComponent from './InvoiceComponent';
import CollapsibleInvoice from './CollapsibleInvoice';
import { useGetQueueServicesListQuery } from '../store/api/QueueApi';
import { getToken } from '../utils';

const ServicesSection = ({
  queueUserDetail,
  isAdmin = false,
  navigation,
  onSettleOffline = null,
  styles,
  quantityLimitsData,
  isQuantityLimitsLoading,
  queueServicesData,
  isQueueServicesLoading,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);

  // Use passed queueServicesData if available, otherwise make API call
  const {
    data: internalQueueServicesData,
    isLoading: internalIsQueueServicesLoading,
  } = useGetQueueServicesListQuery(
    !queueServicesData && queueUserDetail?.queue_id
      ? { id: queueUserDetail.queue_id }
      : null,
    { skip: !queueUserDetail?.queue_id || !!queueServicesData }
  );

  // Use passed data if available, otherwise use internal API call
  const finalQueueServicesData = queueServicesData || internalQueueServicesData;
  const finalIsQueueServicesLoading =
    isQueueServicesLoading || internalIsQueueServicesLoading;

  if (!queueUserDetail?.queue_services_enabled) {
    return null;
  }

  // Normalize various possible shapes to an array of services
  const toServiceArray = (servicesOrResponse) => {
    if (Array.isArray(servicesOrResponse)) return servicesOrResponse;
    if (servicesOrResponse && Array.isArray(servicesOrResponse.services)) {
      return servicesOrResponse.services;
    }
    return [];
  };

  // Helper function to check if all services are auto-included based on API response
  const areAllServicesAutoIncluded = (servicesOrResponse) => {
    const services = toServiceArray(servicesOrResponse);
    if (!services || services.length === 0) return false;
    return services.every((service) => service.is_auto_included === true);
  };

  // Check if we should hide the entire services section based on API response
  const shouldHideEntireSection = () => {
    const services = toServiceArray(finalQueueServicesData);
    if (!services || services.length === 0) {
      return false;
    }

    // Hide if all services from API are auto-included
    const shouldHide = areAllServicesAutoIncluded(services);
    return shouldHide;
  };

  // Hide entire section if all services are auto-included
  if (shouldHideEntireSection()) {
    return null;
  }

  const getUserPurchasedServiceIds = () => {
    const serviceIds = [];

    if (queueUserDetail?.service_orders) {
      queueUserDetail.service_orders.forEach((serviceOrder) => {
        if (serviceOrder.services) {
          serviceOrder.services.forEach((service) => {
            if (!serviceIds.includes(service.id)) {
              serviceIds.push(service.id);
            }
          });
        }
      });
    }

    if (queueUserDetail?.services) {
      queueUserDetail.services.forEach((service) => {
        if (!serviceIds.includes(service.id)) {
          serviceIds.push(service.id);
        }
      });
    }

    return serviceIds;
  };

  const shouldShowBuyMoreButton = () => {
    if (queueUserDetail?.queue_type !== 'Batch') {
      return false;
    }
    if (isAdmin) {
      return true; // Admin always sees buy more button
    }
    if (!queueUserDetail?.queue_services_enabled) {
      return false;
    }

    // Check if all services from API are auto-included
    const services = toServiceArray(finalQueueServicesData);
    if (services && services.length > 0) {
      const allAutoIncluded = areAllServicesAutoIncluded(services);
      if (allAutoIncluded) {
        return false; // Don't show buy more if all services are auto-included
      }
    }

    return true;
  };

  const handleBuyMoreServices = async () => {
    let isLoggedIn = false;
    if (user && (user.id || user.email || user.name)) {
      isLoggedIn = true;
    } else {
      try {
        const token = await getToken();
        isLoggedIn = !!token;
      } catch (_) {
        isLoggedIn = false;
      }
    }

    if (isLoggedIn) {
      navigation.navigate('BuyMoreServicesScreen', {
        qid: queueUserDetail?.queue_id,
        queue_user_id: queueUserDetail?.id,
        isAdmin,
      });
    } else {
      navigation.navigate('SignInScreen');
    }
  };

  const renderBuyMoreButton = () => {
    // Don't show button while loading quantity limits
    if (isQuantityLimitsLoading) {
      return (
        <View style={styles.buyMoreServicesHeader}>
          <QText style={styles.servicesTitle}>{t('services')}</QText>
        </View>
      );
    }

    if (!shouldShowBuyMoreButton()) {
      return null;
    }

    const userPurchasedServiceIds = getUserPurchasedServiceIds();
    const hasAlreadyBoughtServices = userPurchasedServiceIds.length > 0;

    return (
      <View style={styles.buyMoreServicesHeader}>
        <QText style={styles.servicesTitle}>{t('services')}</QText>
        <TouchableOpacity
          onPress={handleBuyMoreServices}
          style={styles.buyMoreServicesLink}
        >
          <QText style={styles.buyMoreServicesText}>
            + {hasAlreadyBoughtServices ? t('buy_more') : t('buy_services')}
          </QText>
        </TouchableOpacity>
      </View>
    );
  };

  // Helper function to check if all services have null prices
  const areAllServicesFree = (services) => {
    if (!services || services.length === 0) return false;
    return services.every((service) => service.price === null);
  };

  // Helper function to check if all services are free AND auto-included
  const areAllServicesFreeAndAutoIncluded = (services) => {
    if (!services || services.length === 0) return false;
    return services.every(
      (service) => service.price === null && service.is_auto_included === true
    );
  };

  // Helper function to check if any service order has all free services
  const hasAnyFreeServiceOrder = () => {
    if (queueUserDetail?.service_orders) {
      return queueUserDetail.service_orders.some(
        (serviceOrder) =>
          serviceOrder.services && areAllServicesFree(serviceOrder.services)
      );
    }
    return false;
  };

  // Helper function to check if any service order has all free AND auto-included services
  const hasAnyFreeAndAutoIncludedServiceOrder = () => {
    if (queueUserDetail?.service_orders) {
      return queueUserDetail.service_orders.some(
        (serviceOrder) =>
          serviceOrder.services &&
          areAllServicesFreeAndAutoIncluded(serviceOrder.services)
      );
    }
    return false;
  };

  // Helper function to check if standalone services are all free
  const areStandaloneServicesFree = () => {
    return (
      queueUserDetail?.services && areAllServicesFree(queueUserDetail.services)
    );
  };

  // Helper function to check if standalone services are all free AND auto-included
  const areStandaloneServicesFreeAndAutoIncluded = () => {
    return (
      queueUserDetail?.services &&
      areAllServicesFreeAndAutoIncluded(queueUserDetail.services)
    );
  };

  // Helper function to check if all services have null price
  const areAllServicesWithNullPrice = (services) => {
    return (
      services &&
      services.length > 0 &&
      services.every((service) => service.price === null)
    );
  };

  // Helper function to filter out services with null price
  const filterServicesWithValidPrice = (services) => {
    return services ? services.filter((service) => service.price !== null) : [];
  };

  const renderServices = () => {
    const invoiceComponents = [];

    if (queueUserDetail?.queue_type === 'Batch') {
      // Batch queue service rendering logic
      if (
        queueUserDetail?.service_orders &&
        queueUserDetail?.service_orders.length > 0
      ) {
        queueUserDetail.service_orders.forEach((serviceOrder, index) => {
          const hasServices =
            serviceOrder.services && serviceOrder.services.length > 0;

          // Skip this service order if all services have null price
          if (
            hasServices &&
            areAllServicesWithNullPrice(serviceOrder.services)
          ) {
            return;
          }

          let orderLabel;
          if (index === 0) {
            orderLabel = t('original_services');
          } else {
            orderLabel = `${t('additional_services')} #${index}`;
          }

          const subtitle = `${t('order_no')}: ${serviceOrder.service_order_no}`;
          const paymentAmount = serviceOrder.payment_transaction?.amount || '0';
          const paymentStatus =
            serviceOrder.payment_transaction?.status || 'Unknown';

          // Calculate if there are any discounts in the services
          const hasDiscounts =
            hasServices &&
            serviceOrder.services.some(
              (service) =>
                service.discount_percentage && service.discount_percentage > 0
            );

          invoiceComponents.push(
            <CollapsibleInvoice
              key={serviceOrder.id}
              title={orderLabel}
              subtitle={subtitle}
              isExpanded={index === 0}
              paymentAmount={paymentAmount}
              paymentStatus={paymentStatus}
              showAmountInHeader={true}
              hasDiscounts={hasDiscounts}
              services={serviceOrder.services}
            >
              {hasServices ? (
                <InvoiceComponent
                  mode="confirmed"
                  selectedServices={filterServicesWithValidPrice(
                    serviceOrder.services
                  )}
                  serviceOrder={serviceOrder}
                  queueDetail={{
                    name: queueUserDetail?.queue_name,
                    description: queueUserDetail?.queue_description,
                  }}
                  onNext={() => {}}
                  onBack={() => {}}
                  showQueueInfo={false}
                  hideBackButton={true}
                  fullWidth={true}
                  showFooterButtons={false}
                  serviceOrderId={serviceOrder.id}
                  paymentId={serviceOrder.payment_transaction?.id}
                  paymentStatus={
                    serviceOrder.payment_transaction?.status || 'pending'
                  }
                  onSettleOffline={onSettleOffline}
                  onError={(error) => {
                    console.error(
                      'TESTING_LOG - ServicesSection invoice error:',
                      error
                    );
                  }}
                />
              ) : (
                <View style={styles.emptyServiceOrderContainer}>
                  <QText style={styles.emptyServiceOrderText}>
                    {t('no_services_in_order')}
                  </QText>
                  <QText style={styles.emptyServiceOrderSubtext}>
                    {t('this_order_has_no_services_but_payment_was_made')}
                  </QText>
                </View>
              )}
            </CollapsibleInvoice>
          );
        });
      } else if (
        queueUserDetail?.services &&
        queueUserDetail?.services.length > 0
      ) {
        // Fallback: if no service_orders but services exist
        // Skip if all services are free AND auto-included
        if (!areStandaloneServicesFreeAndAutoIncluded()) {
          invoiceComponents.push(
            <CollapsibleInvoice
              key="standalone-services"
              title={t('services')}
              subtitle=""
              isExpanded={true}
              paymentAmount="0"
              paymentStatus="success"
              showAmountInHeader={false}
            >
              <InvoiceComponent
                mode="preview"
                selectedServices={queueUserDetail.services}
                queueDetail={{
                  name: queueUserDetail?.queue_name,
                  description: queueUserDetail?.queue_description,
                }}
                onNext={() => {}}
                onBack={() => {}}
                showQueueInfo={false}
                hideBackButton={true}
                fullWidth={true}
                showFooterButtons={false}
                paymentStatus="success"
                onSettleOffline={null}
                onError={(error) => {
                  console.error(
                    'TESTING_LOG - ServicesSection preview mode error:',
                    error
                  );
                }}
              />
            </CollapsibleInvoice>
          );
        }
      } else {
        // Batch queue with no purchased services
        invoiceComponents.push(
          <View
            key="no-services-message"
            style={styles.emptyServiceOrderContainer}
          >
            <QText style={styles.emptyServiceOrderText}>
              {t('no_services_selected')}
            </QText>
            <QText style={styles.emptyServiceOrderSubtext}>
              {t('you_can_add_services_using_buy_more_button')}
            </QText>
          </View>
        );
      }
    } else {
      // Non-Batch queue service rendering logic
      if (
        queueUserDetail?.service_orders &&
        queueUserDetail?.service_orders.length > 0
      ) {
        const serviceOrder = queueUserDetail.service_orders[0];
        const hasServices =
          serviceOrder.services && serviceOrder.services.length > 0;

        // Skip if all services have null price
        if (hasServices && areAllServicesWithNullPrice(serviceOrder.services)) {
          return [];
        }

        const subtitle = `${t('order_no')}: ${serviceOrder.service_order_no}`;
        const paymentAmount = serviceOrder.payment_transaction?.amount || '0';
        const paymentStatus =
          serviceOrder.payment_transaction?.status || 'Unknown';

        invoiceComponents.push(
          <CollapsibleInvoice
            key="non-batch-services"
            title={t('services')}
            subtitle={subtitle}
            isExpanded={true}
            paymentAmount={paymentAmount}
            paymentStatus={paymentStatus}
            showAmountInHeader={true}
          >
            {hasServices ? (
              <InvoiceComponent
                mode="confirmed"
                selectedServices={filterServicesWithValidPrice(
                  serviceOrder.services
                )}
                serviceOrder={serviceOrder}
                queueDetail={{
                  name: queueUserDetail?.queue_name,
                  description: queueUserDetail?.queue_description,
                }}
                onNext={() => {}}
                onBack={() => {}}
                showQueueInfo={false}
                hideBackButton={true}
                fullWidth={true}
                showFooterButtons={false}
                serviceOrderId={serviceOrder.id}
                paymentStatus={
                  serviceOrder.payment_transaction?.status || 'pending'
                }
                onSettleOffline={null}
                onError={(error) => {
                  console.error(
                    'TESTING_LOG - ServicesSection confirmed mode error:',
                    error
                  );
                }}
              />
            ) : (
              <View style={styles.emptyServiceOrderContainer}>
                <QText style={styles.emptyServiceOrderText}>
                  {t('no_services_in_order')}
                </QText>
                <QText style={styles.emptyServiceOrderSubtext}>
                  {t('this_order_has_no_services_but_payment_was_made')}
                </QText>
              </View>
            )}
          </CollapsibleInvoice>
        );
      } else if (
        queueUserDetail?.services &&
        queueUserDetail?.services.length > 0
      ) {
        // Skip if all standalone services have null price
        if (areAllServicesWithNullPrice(queueUserDetail.services)) {
          return [];
        }

        invoiceComponents.push(
          <CollapsibleInvoice
            key="fallback-services"
            title={t('services')}
            subtitle={isAdmin ? '' : t('service_details')}
            isExpanded={true}
            paymentAmount="0"
            paymentStatus="success"
            showAmountInHeader={false}
          >
            <InvoiceComponent
              mode="preview"
              selectedServices={filterServicesWithValidPrice(
                queueUserDetail.services
              )}
              queueDetail={{
                name: queueUserDetail?.queue_name,
                description: queueUserDetail?.queue_description,
              }}
              onNext={() => {}}
              onBack={() => {}}
              showQueueInfo={false}
              hideBackButton={true}
              fullWidth={true}
              showFooterButtons={false}
              paymentStatus="success"
              onSettleOffline={null}
              onError={(error) => {
                console.error(
                  'TESTING_LOG - ServicesSection fallback preview mode error:',
                  error
                );
              }}
            />
          </CollapsibleInvoice>
        );
      } else {
        invoiceComponents.push(
          <View
            key="no-services-message"
            style={styles.emptyServiceOrderContainer}
          >
            <QText style={styles.emptyServiceOrderText}>
              {isAdmin ? t('no_services_selected') : t('no_services_available')}
            </QText>
            <QText style={styles.emptyServiceOrderSubtext}>
              {isAdmin
                ? t('you_can_add_services_using_buy_more_button')
                : t('this_queue_does_not_have_services_configured')}
            </QText>
          </View>
        );
      }
    }

    return invoiceComponents;
  };

  return (
    <View style={styles.sectionContainer}>
      {isAdmin ? (
        renderBuyMoreButton()
      ) : (
        <View style={styles.sectionPartHeadingContainer}>
          <QText style={styles.sectionPartHeadingText}>{t('services')}</QText>
        </View>
      )}

      {!isAdmin && renderBuyMoreButton()}
      {renderServices()}
    </View>
  );
};

export default ServicesSection;
